import { Injectable } from '@angular/core';
import { currentUser } from 'auth-service-lib';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { BullhornEntityType } from '../types/bullhorn/bullhorn-entity-type';
import { BullhornDataService } from './bullhorn-data.service';

export interface IUserRestrictionAttributes {
  userTypeId: string;
  departmentIds: string[];
  privateLabelIds: string[];
}

const FALLBACK_ATTRIBUTES: IUserRestrictionAttributes = {
  userTypeId: '0',
  departmentIds: [],
  privateLabelIds: [],
};

@Injectable({ providedIn: 'root' })
export class BullhornPromptRestrictionsService {
  private userAttributes$: Observable<IUserRestrictionAttributes> | null = null;

  constructor(private bullhornDataService: BullhornDataService) {}

  getCurrentUserAttributes(): Observable<IUserRestrictionAttributes> {
    if (this.userAttributes$) {
      return this.userAttributes$;
    }
    // calling the endpoint to get fields
    this.userAttributes$ = forkJoin({
      users: this.bullhornDataService.query(
        BullhornEntityType.CORPORATE_USER,`id=${currentUser.id}`,['id', 'userType', 'privateLabel'],
      ),
      departments: this.bullhornDataService.getAssociation(
        BullhornEntityType.CORPORATE_USER, Number(currentUser.id),'departments',
      ),
      privateLabels: this.bullhornDataService.getAssociation(
        BullhornEntityType.CORPORATE_USER, Number(currentUser.id),'privateLabels',
      ),
    }).pipe(
      map(({ users, departments, privateLabels }) =>
        this.mapToAttributes(users, departments, privateLabels),
      ),
      catchError((err) => {
        console.error('BullhornPromptRestrictionsService: query failed', err);
        return of(FALLBACK_ATTRIBUTES);
      }),
      shareReplay(1)
    );
    return this.userAttributes$;
  }

  // map bullhorn response to IUserRestrictionAttributes
  private mapToAttributes(
    users: any[],
    departments: any[],
    privateLabels: any[],
  ): IUserRestrictionAttributes {
    const user = users[0];
    return {
      userTypeId: String(user?.userType?.id ?? 0),
      departmentIds: departments.map((department: any) => String(department.id)),
      privateLabelIds: [
        String(user?.privateLabel?.id ?? 0),
        ...privateLabels.map((privateLabel: any) => String(privateLabel.id)),
      ],
    };
  }
}
